import { Component } from '@angular/core';
import { logUserAction } from '@office-start/telemetry-service';
import { ActionResult, ActionTarget, Area, Referral } from '@office-start/telemetry-types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { OfficeRoute } from '../shared/constants/office-route.enum';
import { KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { ThemingService } from '../shared/services/theming.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { LocService } from '../shared/shared-context/loc.service';

import './bottom-tray.component.css';

export interface BottomTrayTab {
    readonly id: string;
    readonly title: string;
    readonly ariaLabel: string;
    readonly routes: Array<OfficeRoute>;
    readonly iconClass: string;
}

const homeTabId = 'home';
const searchTabId = 'search';
const appsTabId = 'apps';

@Component({
    selector: 'ohp-bottom-tray',
    templateUrl: './bottom-tray.component.html'
})
export class BottomTrayComponent {
    public static readonly LastBottomTrayTab = 'LastBottomTrayTab';
    public readonly tabs: Array<BottomTrayTab>;
    public activeTabId: Observable<string>;
    public isDarkMode: Observable<boolean>;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly themingService: ThemingService,
        private readonly locService: LocService
    ) {
        const homeTab = {
            id: homeTabId,
            title: this.locService.HomeTabTitle,
            ariaLabel: this.locService.HomeTabAriaLabel,
            routes: [OfficeRoute.OfficeHome, OfficeRoute.Start, OfficeRoute.Templates],
            iconClass: 'Home'
        };

        const searchTab = {
            id: searchTabId,
            title: this.locService.SearchTabTitle,
            ariaLabel: this.locService.SearchTabAriaLabel,
            routes: [OfficeRoute.Search],
            iconClass: 'Search'
        };

        const appsTab = {
            id: appsTabId,
            title: this.locService.AppsTabTitle,
            ariaLabel: this.locService.AppsTabAriaLabel,
            routes: [OfficeRoute.AppGallery],
            iconClass: 'Waffle'
        };

        this.tabs = this.capabilitiesService.capabilities.showSearch ? [homeTab, searchTab, appsTab] : [homeTab, appsTab];

        this.activeTabId = officeRouteService.getRouteChangeStartObservable().pipe(map(event => {
            const route = event.currentRoute.route;
            const activeTab = this.tabs.find(tab => this.isActive(tab, route));
            return activeTab ? activeTab.id : 'Home';
        }));

        this.isDarkMode = this.themingService.themeShades.pipe(
            map(event => event.theme && event.theme.reactShellTheme ? event.theme.reactShellTheme.IsDarkMode : false)
        );
    }

    public trackByTabId(_: number, tab: BottomTrayTab) {
        return tab.id;
    }

    public navigateTo(tab: BottomTrayTab) {
        let result: ActionResult = 'Navigate';
        let target: ActionTarget = 'Other';
        if (tab.id === homeTabId) {
            result = 'Refer';
            target = Referral.OfficeDotCom;
        } else if (tab.id === searchTabId) {
            result = 'NavigateTo';
            target = 'SearchResults';
        } else if (tab.id === appsTabId) {
            result = 'NavigateTo';
            target = Referral.AppGallery;
        }
        logUserAction({
            eventName: KnownInstrumentationEvents.BottomTrayClick,
            id: tab.id,
            area: 'BottomTray' as Area,
            result,
            target
        });
        // The first route is the default load
        this.officeRouteService.navigateTo({ route: tab.routes[0] }, false, null, BottomTrayComponent.LastBottomTrayTab);
    }

    public isActive(tab: BottomTrayTab, route: OfficeRoute): boolean {
        return tab.routes.indexOf(route) >= 0;
    }
}
